/**
 * Vaperite webhook payloads (Admin Tools)
 *
 * View, rerun and delete the stored Vaperite menu / item webhook payloads, grouped
 * by store and bucket. Moved here unchanged from the Vaperite Staging Dashboard's
 * former "Webhook Payloads" tab (PR 2, Task 9) — same endpoints, same behaviour.
 *
 * Access: Owner and Admin roles only
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Timer as TimerIcon,
  ExpandMore as ExpandIcon,
  Store as StoreIcon,
  Visibility as ViewIcon,
  FiberManualRecord as DotIcon,
  Replay as ReplayIcon,
  DeleteOutline as DeletePayloadIcon,
  CloudQueue as WebhookIcon,
  Description as DescriptionIcon,
  SyncAlt as SyncIcon,
  BoltOutlined as BoltIcon,
  History as HistoryIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import {
  useVaperiteProcessingProgress,
  VaperiteProcessingProgressPanel,
} from '../../components/VaperiteProcessingProgress';
import { usePermissions } from '../../context/PermissionContext';
import { useAdminTheme } from '../../admin-theme';
import Logger from '../../utils/Logger';
import {
  DashboardMetrics,
  GlowCard,
  SystemHealthPanel,
  darkTableSx,
  formatTimeAgo,
} from '../vaperite/ui';

// ============================================================================
// Types — camelCase to match backend staging-repository.ts
// ============================================================================

interface ProcessingResultSummary {
  processedImmediately: number;
  stagedForApproval: number;
  errors: number;
  errorMessages?: string[];
  fieldsStaged?: number;
  newProducts?: number;
  stockUpdates?: number;
  priceChanges?: number;
}

interface RunHistoryEntry {
  runNumber: number;
  trigger: 'webhook' | 'manual';
  startedAt: string;
  completedAt?: string;
  result?: ProcessingResultSummary;
  error?: string;
}

interface PayloadEntry {
  id: string;
  eventType: string;
  itemCount: number;
  categoryCount: number;
  sizeKB: number;
  status: 'stored' | 'processing' | 'processed' | 'failed';
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  runCount?: number;
  lastResult?: ProcessingResultSummary | null;
  storeName?: string;
}

interface PayloadStoreGroup {
  externalStoreId: string;
  storeName: string;
  storeId: string | null;
  payloads: PayloadEntry[];
}

interface PayloadStoreSummary {
  externalStoreId: string;
  storeName: string;
  storeId: string | null;
  webhookCount: number;
  menuUpdateCount: number;
  totalCount: number;
}

interface TodaysActivityData {
  recentWebhooks: Array<PayloadEntry & { storeName: string }>;
  lastMenuSync: (PayloadEntry & { storeName: string }) | null;
}

interface BucketState {
  entries: PayloadEntry[];
  total: number;
  page: number;
  loading: boolean;
}

const BUCKET_PAGE_SIZE = 20;

/** Compact result summary chips for a single processing run */
const ResultSummaryChips: React.FC<{ result: ProcessingResultSummary; compact?: boolean }> = ({ result, compact }) => {
  const { colors } = useAdminTheme();
  const chipSx = {
    fontWeight: 600,
    fontSize: compact ? '0.6rem' : '0.65rem',
    height: compact ? 20 : 22,
    '& .MuiChip-label': { px: compact ? 0.75 : 1 },
  };
  const items: Array<{ label: string; value: number; color: string }> = [
    { label: 'immediate', value: result.processedImmediately, color: colors.success },
    { label: 'staged', value: result.stagedForApproval, color: colors.info },
    { label: 'errors', value: result.errors, color: colors.error },
  ];
  const extra: Array<{ label: string; value: number | undefined; color: string }> = [
    { label: 'fields', value: result.fieldsStaged, color: colors.info },
    { label: 'new products', value: result.newProducts, color: colors.purple },
    { label: 'stock', value: result.stockUpdates, color: colors.success },
    { label: 'price', value: result.priceChanges, color: colors.warning },
  ];
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {items.filter((i) => i.value > 0).map((i) => (
        <Chip
          key={i.label}
          label={`${i.value} ${i.label}`}
          size="small"
          sx={{ ...chipSx, bgcolor: alpha(i.color, 0.12), color: i.color, border: `1px solid ${alpha(i.color, 0.25)}` }}
        />
      ))}
      {!compact && extra.filter((i) => i.value != null && i.value > 0).map((i) => (
        <Chip
          key={i.label}
          label={`${i.value} ${i.label}`}
          size="small"
          sx={{ ...chipSx, bgcolor: alpha(i.color!, 0.08), color: i.color, border: `1px solid ${alpha(i.color!, 0.2)}` }}
        />
      ))}
    </Stack>
  );
};

/** Format duration between two ISO timestamps */
const formatDuration = (start: string, end?: string): string => {
  if (!end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

interface Props {
  onBack: () => void;
}

const WebhookPayloads: React.FC<Props> = ({ onBack }) => {
  const { colors, glassHeaderStyle, elevatedInputStyle } = useAdminTheme();
  const { role, loading: permissionsLoading } = usePermissions();
  const notify = useNotify();
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const [payloadStores, setPayloadStores] = useState<PayloadStoreSummary[]>([]);
  const [payloadStoresLoading, setPayloadStoresLoading] = useState(false);
  const [todaysActivity, setTodaysActivity] = useState<TodaysActivityData>({ recentWebhooks: [], lastMenuSync: null });
  const [payloadsStatus, setPayloadsStatus] = useState<string>('');
  const [payloadsStoreFilter, setPayloadsStoreFilter] = useState<string>('');
  const [bucketCache, setBucketCache] = useState<Record<string, BucketState>>({});
  const [rerunningId, setRerunningId] = useState<string | null>(null);
  const [payloadViewOpen, setPayloadViewOpen] = useState(false);
  const [payloadViewData, setPayloadViewData] = useState<any>(null);
  const [payloadViewLoading, setPayloadViewLoading] = useState(false);
  const [expandedPayloadStores, setExpandedPayloadStores] = useState<Set<string>>(new Set());
  const [expandedPayloadEventTypes, setExpandedPayloadEventTypes] = useState<Set<string>>(new Set());

  // Rich metrics (System Health panel)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);

  // Ref to the latest fetch function (for the onComplete callback)
  const fetchPayloadStoresRef = useRef<() => void>(() => {});

  // Processing progress (shared Socket.IO live panel)
  const processingProgress = useVaperiteProcessingProgress({
    onComplete: () => {
      fetchPayloadStoresRef.current();
    },
  });

  // ------ Data Fetching ------

  const fetchPayloadStores = useCallback(async (resetUI = false) => {
    setPayloadStoresLoading(true);
    try {
      const params: Record<string, any> = {};
      if (payloadsStatus) params.status = payloadsStatus;
      const { data } = await authInstance.get('vaperite/staging/payloads/stores', { params });
      setPayloadStores(data.stores || []);
      setTodaysActivity(data.todaysActivity || { recentWebhooks: [], lastMenuSync: null });
      // Only reset UI state on initial load or filter change, not on polling refreshes
      if (resetUI) {
        setExpandedPayloadStores(new Set((data.stores || []).map((s: PayloadStoreSummary) => s.externalStoreId)));
        setExpandedPayloadEventTypes(new Set());
        setBucketCache({});
      }
    } catch (err: any) {
      Logger.error('Failed to fetch payload stores:', err);
    } finally {
      setPayloadStoresLoading(false);
    }
  }, [payloadsStatus]);

  const fetchMetrics = useCallback(async () => {
    try {
      const { data } = await authInstance.get('vaperite/staging/metrics');
      setMetrics(data);
    } catch (err: any) {
      Logger.error('Failed to fetch metrics:', err);
    }
  }, []);

  const fetchBucketData = useCallback(async (storeId: string, bucket: 'webhooks' | 'menu_updates', page: number = 1) => {
    const cacheKey = `${storeId}::${bucket}`;
    setBucketCache((prev) => ({
      ...prev,
      [cacheKey]: { ...(prev[cacheKey] || { entries: [], total: 0, page: 0 }), loading: true },
    }));
    try {
      const params: Record<string, any> = { store: storeId, bucket, page, limit: BUCKET_PAGE_SIZE };
      if (payloadsStatus) params.status = payloadsStatus;
      const { data } = await authInstance.get('vaperite/staging/payloads', { params });
      const newEntries: PayloadEntry[] = (data.stores || []).flatMap((s: PayloadStoreGroup) =>
        s.payloads.map((p) => ({ ...p, storeName: p.storeName ?? s.storeName })),
      );
      setBucketCache((prev) => {
        const existing = prev[cacheKey];
        return {
          ...prev,
          [cacheKey]: {
            entries: page === 1 ? newEntries : [...(existing?.entries || []), ...newEntries],
            total: data.total || 0,
            page,
            loading: false,
          },
        };
      });
    } catch (err: any) {
      Logger.error(`Failed to fetch ${bucket} for store ${storeId}:`, err);
      setBucketCache((prev) => ({
        ...prev,
        [cacheKey]: { ...(prev[cacheKey] || { entries: [], total: 0, page: 0 }), loading: false },
      }));
    }
  }, [payloadsStatus]);

  const refreshAll = useCallback(() => {
    fetchMetrics();
    fetchPayloadStores();
    setLastUpdate(new Date());
  }, [fetchMetrics, fetchPayloadStores]);

  // Keep ref in sync for Socket.IO callback
  fetchPayloadStoresRef.current = fetchPayloadStores;

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);
  // Initial load and status-filter change reset the expanded/bucket UI state
  useEffect(() => {
    fetchPayloadStores(true);
  }, [fetchPayloadStores]);
  useEffect(() => {
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  // ------ Actions ------

  const handleRerunPayload = async (id: string, storeName?: string | null) => {
    setRerunningId(id);
    try {
      const { data } = await authInstance.post(`vaperite/staging/payloads/${id}/rerun`);
      if (data.success) {
        // Immediately inject a synthetic progress entry so the panel appears
        processingProgress.addPayload(data.payloadId ?? id, storeName ?? null);
        notify('Payload queued for re-processing', { type: 'success' });
      } else {
        notify(`Rerun failed: ${data.error}`, { type: 'warning' });
      }
    } catch (err: any) {
      notify(`Rerun failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setRerunningId(null);
    }
  };

  const handleDeletePayload = async (id: string) => {
    if (!window.confirm('Delete this payload? This cannot be undone.')) return;
    try {
      await authInstance.delete(`vaperite/staging/payloads/${id}`);
      notify('Payload deleted', { type: 'success' });
      fetchPayloadStores();
    } catch (err: any) {
      notify(`Delete failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    }
  };

  const handleViewPayload = async (id: string) => {
    setPayloadViewLoading(true);
    setPayloadViewOpen(true);
    try {
      const { data } = await authInstance.get(`vaperite/staging/payloads/${id}`);
      setPayloadViewData(data);
    } catch (err: any) {
      notify('Failed to load payload', { type: 'error' });
      setPayloadViewOpen(false);
    } finally {
      setPayloadViewLoading(false);
    }
  };

  const togglePayloadStore = (storeId: string) => {
    setExpandedPayloadStores((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const togglePayloadEventType = (compositeKey: string) => {
    const isCurrentlyExpanded = expandedPayloadEventTypes.has(compositeKey);
    setExpandedPayloadEventTypes((prev) => {
      const next = new Set(prev);
      if (next.has(compositeKey)) next.delete(compositeKey);
      else next.add(compositeKey);
      return next;
    });
    // Auto-fetch on first expand
    if (!isCurrentlyExpanded && !bucketCache[compositeKey]) {
      const [storeId, bucket] = compositeKey.split('::') as [string, 'webhooks' | 'menu_updates'];
      fetchBucketData(storeId, bucket);
    }
  };

  // ------ Derived data ------

  // Webhook-specific stats for summary cards when on payloads tab
  const webhookStats = useMemo(() => {
    let menuUpdates = 0;
    let webhooks = 0;
    let totalPayloads = 0;
    payloadStores.forEach((s) => {
      menuUpdates += s.menuUpdateCount;
      webhooks += s.webhookCount;
      totalPayloads += s.totalCount;
    });
    return { menuUpdates, webhooks, storeCount: payloadStores.length, totalPayloads };
  }, [payloadStores]);

  // Store options for filter dropdown
  const payloadStoreOptions = useMemo(() => {
    return payloadStores.map((s) => ({ id: s.externalStoreId, name: s.storeName || s.externalStoreId }));
  }, [payloadStores]);

  // Filtered store list (client-side store filter)
  const filteredPayloadStores = useMemo(() => {
    if (!payloadsStoreFilter) return payloadStores;
    return payloadStores.filter((s) => s.externalStoreId === payloadsStoreFilter);
  }, [payloadStores, payloadsStoreFilter]);

  // ------ Permission Check ------

  if (permissionsLoading) {
    return (
      <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Staff reach a page only through its tick (PageGate), so a ticked page is theirs to use
  const hasAccess = role === 'owner' || role === 'admin' || role === 'staff';
  if (!hasAccess) {
    return (
      <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
        <Button startIcon={<BackIcon />} onClick={onBack} sx={{ mb: 2 }}>Back to Tools</Button>
        <Alert severity="error" sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error, border: `1px solid ${alpha(colors.error, 0.3)}` }}>
          Access Denied: This page is only accessible by owners and administrators.
        </Alert>
      </Box>
    );
  }

  // ======================================================================
  // RENDER
  // ======================================================================

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Box sx={{ ...glassHeaderStyle, px: 3, py: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Button startIcon={<BackIcon />} onClick={onBack} sx={{ color: colors.textSecondary }}>
              Back
            </Button>
            <Box>
              <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 800, letterSpacing: '-0.02em' }}>
                Vaperite webhook payloads
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.25 }}>
                View, rerun, and delete stored Vaperite webhook payloads
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {lastUpdate && (
              <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                {lastUpdate.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh">
              <IconButton
                aria-label="Refresh"
                onClick={refreshAll}
                sx={{
                  color: colors.textSecondary,
                  bgcolor: colors.bgElevated,
                  border: `1px solid ${colors.border}`,
                  '&:hover': { color: colors.info, borderColor: alpha(colors.info, 0.4) },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 3, pb: 4 }}>
        {/* Webhook stats */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="Total Payloads"
              value={webhookStats.totalPayloads}
              color={colors.info}
              icon={<WebhookIcon />}
              subtitle="All received webhook payloads"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="Active Stores"
              value={webhookStats.storeCount}
              color={colors.purple}
              icon={<StoreIcon />}
              subtitle="Stores sending webhook data"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="Item Webhooks"
              value={webhookStats.webhooks}
              color={colors.warning}
              icon={<BoltIcon />}
              subtitle="Real-time item updates"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="Menu Syncs"
              value={webhookStats.menuUpdates}
              color={colors.success}
              icon={<SyncIcon />}
              subtitle="Full menu sync payloads"
            />
          </Grid>
        </Grid>

        {/* Processing Progress Panel (live Socket.IO updates) */}
        <VaperiteProcessingProgressPanel {...processingProgress} />

        <Box sx={{ py: 3 }}>
          {/* System Health */}
          <SystemHealthPanel metrics={metrics} variant="webhooks" />

          {/* Filter bar */}
          <Paper sx={{
            p: 2,
            mb: 2.5,
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}>
            <FormControl size="small" sx={{ minWidth: 140, ...elevatedInputStyle }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={payloadsStatus}
                onChange={(e) => setPayloadsStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="stored">Stored</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160, ...elevatedInputStyle }}>
              <InputLabel>Store</InputLabel>
              <Select
                value={payloadsStoreFilter}
                onChange={(e) => setPayloadsStoreFilter(e.target.value)}
                label="Store"
              >
                <MenuItem value="">All Stores</MenuItem>
                {payloadStoreOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              {webhookStats.totalPayloads} payload{webhookStats.totalPayloads !== 1 ? 's' : ''} across {webhookStats.storeCount} store{webhookStats.storeCount !== 1 ? 's' : ''}
            </Typography>
          </Paper>

          {/* Today's Activity */}
          {!payloadStoresLoading && (todaysActivity.recentWebhooks.length > 0 || todaysActivity.lastMenuSync) && (
            <Paper sx={{
              p: 2,
              mb: 2.5,
              bgcolor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
            }}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon sx={{ fontSize: 18, color: colors.info }} />
                Today&apos;s Activity
                <Chip label={`${todaysActivity.recentWebhooks.length + (todaysActivity.lastMenuSync ? 1 : 0)} recent`} size="small" sx={{ bgcolor: alpha(colors.info, 0.1), color: colors.info, fontWeight: 600, fontSize: '0.6rem', height: 20 }} />
              </Typography>
              <Grid container spacing={2}>
                {/* Last Menu Sync */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ p: 1.5, bgcolor: alpha(colors.success, 0.04), borderRadius: 1.5, border: `1px solid ${alpha(colors.success, 0.15)}` }}>
                    <Typography variant="caption" sx={{ color: colors.success, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.05em' }}>
                      Last Menu Sync
                    </Typography>
                    {todaysActivity.lastMenuSync ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.8rem' }}>
                          {todaysActivity.lastMenuSync.storeName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {todaysActivity.lastMenuSync.itemCount} items &middot; {formatTimeAgo(todaysActivity.lastMenuSync.createdAt)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 0.5 }}>
                        No menu sync today
                      </Typography>
                    )}
                  </Box>
                </Grid>
                {/* Recent Item Updates */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Box sx={{ p: 1.5, bgcolor: alpha(colors.warning, 0.04), borderRadius: 1.5, border: `1px solid ${alpha(colors.warning, 0.15)}` }}>
                    <Typography variant="caption" sx={{ color: colors.warning, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.05em' }}>
                      Recent Item Updates
                    </Typography>
                    {todaysActivity.recentWebhooks.length > 0 ? (
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {todaysActivity.recentWebhooks.map((p) => (
                          <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DotIcon sx={{ fontSize: 8, color: colors.warning }} />
                            <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 500, flex: 1 }}>
                              {p.storeName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                              {p.itemCount} item{p.itemCount !== 1 ? 's' : ''}
                            </Typography>
                            <Chip
                              label={p.status.toUpperCase()}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.55rem',
                                fontWeight: 700,
                                bgcolor: p.status === 'processed' ? alpha(colors.success, 0.12) : p.status === 'failed' ? alpha(colors.error, 0.12) : alpha(colors.info, 0.12),
                                color: p.status === 'processed' ? colors.success : p.status === 'failed' ? colors.error : colors.info,
                              }}
                            />
                            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
                              {formatTimeAgo(p.createdAt)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 0.5 }}>
                        No item updates today
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          {payloadStoresLoading ? (
            <Box sx={{ p: 3 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={48} sx={{ bgcolor: colors.bgElevated, mb: 0.5 }} />
              ))}
            </Box>
          ) : filteredPayloadStores.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <WebhookIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 1, opacity: 0.4 }} />
              <Typography variant="body1" sx={{ color: colors.textMuted }}>
                No webhook payloads found
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                Payloads will appear here when Vaperite sends menu_sync webhooks
              </Typography>
            </Box>
          ) : (
            <>
              {filteredPayloadStores.map((store) => {
                const storeExpanded = expandedPayloadStores.has(store.externalStoreId);
                const buckets: Array<{ key: 'webhooks' | 'menu_updates'; label: string; icon: typeof WebhookIcon; color: string; count: number }> = [
                  { key: 'webhooks', label: 'Incoming Webhooks', icon: WebhookIcon, color: colors.warning, count: store.webhookCount },
                  { key: 'menu_updates', label: 'Incoming Menu Updates', icon: RefreshIcon, color: colors.success, count: store.menuUpdateCount },
                ];
                return (
                  <Paper
                    key={store.externalStoreId}
                    sx={{
                      mb: 2,
                      bgcolor: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Store header */}
                    <Box
                      onClick={() => togglePayloadStore(store.externalStoreId)}
                      sx={{
                        p: 1.5,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                        borderBottom: storeExpanded ? `1px solid ${colors.border}` : 'none',
                        '&:hover': { bgcolor: colors.bgElevated },
                        transition: 'all 0.15s',
                      }}
                    >
                      <StoreIcon sx={{ color: colors.info, fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700, flex: 1 }}>
                        {store.storeName || store.externalStoreId}
                      </Typography>
                      <Chip
                        label={`${store.totalCount} payload${store.totalCount !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(colors.info, 0.1),
                          color: colors.info,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                        }}
                      />
                      <ExpandIcon
                        sx={{
                          color: colors.textMuted,
                          fontSize: 20,
                          transform: storeExpanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </Box>

                    {/* Two sub-sections: Incoming Webhooks + Incoming Menu Updates */}
                    <Collapse in={storeExpanded}>
                      {buckets.map(({ key: bucketKey, label, icon: BucketIcon, color, count }) => {
                        if (count === 0) return null;
                        const compositeKey = `${store.externalStoreId}::${bucketKey}`;
                        const isExpanded = expandedPayloadEventTypes.has(compositeKey);
                        const cached = bucketCache[compositeKey];
                        const hasMore = cached ? cached.entries.length < cached.total : true;
                        return (
                          <Box key={compositeKey}>
                            {/* Bucket sub-header */}
                            <Box
                              onClick={() => togglePayloadEventType(compositeKey)}
                              sx={{
                                pl: 4,
                                pr: 2,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                borderBottom: `1px solid ${alpha(colors.border, 0.5)}`,
                                '&:hover': { bgcolor: alpha(colors.bgElevated, 0.5) },
                                transition: 'all 0.15s',
                              }}
                            >
                              <BucketIcon sx={{ color, fontSize: 16 }} />
                              <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 600, flex: 1, fontSize: '0.8rem' }}>
                                {label}
                              </Typography>
                              <Chip
                                label={count}
                                size="small"
                                sx={{
                                  bgcolor: alpha(color, 0.1),
                                  color,
                                  fontWeight: 700,
                                  fontSize: '0.6rem',
                                  height: 20,
                                  minWidth: 28,
                                }}
                              />
                              <ExpandIcon
                                sx={{
                                  color: colors.textMuted,
                                  fontSize: 18,
                                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.2s',
                                }}
                              />
                            </Box>
                            {/* Lazy-loaded bucket payloads */}
                            <Collapse in={isExpanded}>
                              {cached?.loading && !cached.entries.length ? (
                                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                                  <CircularProgress size={24} sx={{ color }} />
                                </Box>
                              ) : cached?.entries.length ? (
                                <>
                                  <TableContainer sx={darkTableSx}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell align="right">Items</TableCell>
                                          <TableCell align="right">Categories</TableCell>
                                          <TableCell align="right">Size</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Result</TableCell>
                                          <TableCell>Error</TableCell>
                                          <TableCell>Time</TableCell>
                                          <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {cached.entries.map((p) => {
                                          const statusMap: Record<string, { color: string; bg: string }> = {
                                            stored: { color: colors.info, bg: alpha(colors.info, 0.12) },
                                            processing: { color: colors.warning, bg: alpha(colors.warning, 0.12) },
                                            processed: { color: colors.success, bg: alpha(colors.success, 0.12) },
                                            failed: { color: colors.error, bg: alpha(colors.error, 0.12) },
                                          };
                                          const st = statusMap[p.status] || { color: colors.textMuted, bg: colors.bgElevated };
                                          return (
                                            <TableRow
                                              key={p.id}
                                              sx={p.status === 'failed' ? { animation: 'skc-error-pulse 3s ease-in-out infinite' } : {}}
                                            >
                                              <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.itemCount}</Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography variant="body2" sx={{ color: p.eventType === 'item_update' ? colors.textMuted : undefined }}>
                                                  {p.eventType === 'item_update' ? '—' : p.categoryCount}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                  {p.sizeKB} KB
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={p.status.toUpperCase()}
                                                  size="small"
                                                  sx={{
                                                    bgcolor: st.bg,
                                                    color: st.color,
                                                    fontWeight: 700,
                                                    fontSize: '0.65rem',
                                                    letterSpacing: '0.05em',
                                                    border: `1px solid ${alpha(st.color, 0.3)}`,
                                                    ...(p.status === 'processing' ? { animation: 'skc-pulse 1.5s ease-in-out infinite' } : {}),
                                                  }}
                                                />
                                              </TableCell>
                                              <TableCell sx={{ maxWidth: 260 }}>
                                                {p.lastResult ? (
                                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                                    {(p.runCount ?? 0) > 1 && (
                                                      <Tooltip title={`${p.runCount} runs`} arrow>
                                                        <Chip
                                                          icon={<HistoryIcon sx={{ fontSize: 12 }} />}
                                                          label={p.runCount}
                                                          size="small"
                                                          sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(colors.info, 0.1), color: colors.info, '& .MuiChip-icon': { color: colors.info } }}
                                                        />
                                                      </Tooltip>
                                                    )}
                                                    <ResultSummaryChips result={p.lastResult} compact />
                                                  </Stack>
                                                ) : (
                                                  <Typography variant="caption" sx={{ color: colors.textMuted }}>—</Typography>
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {p.errorMessage ? (
                                                  <Tooltip title={p.errorMessage} arrow>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        color: colors.error,
                                                        maxWidth: 180,
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help',
                                                      }}
                                                    >
                                                      {p.errorMessage}
                                                    </Typography>
                                                  </Tooltip>
                                                ) : (
                                                  <Typography variant="caption" sx={{ color: colors.textMuted }}>—</Typography>
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                                  {formatTimeAgo(p.createdAt)}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                  <Tooltip title="View full payload">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleViewPayload(p.id)}
                                                      sx={{ color: colors.textMuted, '&:hover': { color: colors.info } }}
                                                    >
                                                      <ViewIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Rerun payload">
                                                    <span>
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => handleRerunPayload(p.id, p.storeName)}
                                                        disabled={rerunningId === p.id}
                                                        sx={{ color: colors.textMuted, '&:hover': { color: colors.warning } }}
                                                      >
                                                        {rerunningId === p.id ? (
                                                          <CircularProgress size={16} sx={{ color: colors.warning }} />
                                                        ) : (
                                                          <ReplayIcon sx={{ fontSize: 18 }} />
                                                        )}
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                  <Tooltip title="Delete payload">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleDeletePayload(p.id)}
                                                      sx={{ color: colors.textMuted, '&:hover': { color: colors.error } }}
                                                    >
                                                      <DeletePayloadIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Stack>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                  {/* Load more button */}
                                  {hasMore && (
                                    <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', borderTop: `1px solid ${alpha(colors.border, 0.3)}` }}>
                                      <Button
                                        size="small"
                                        onClick={() => fetchBucketData(store.externalStoreId, bucketKey, (cached?.page || 1) + 1)}
                                        disabled={cached?.loading}
                                        sx={{
                                          color,
                                          fontSize: '0.75rem',
                                          fontWeight: 600,
                                          '&:hover': { bgcolor: alpha(color, 0.08) },
                                        }}
                                      >
                                        {cached?.loading ? (
                                          <CircularProgress size={14} sx={{ color, mr: 1 }} />
                                        ) : null}
                                        Load more ({cached.entries.length} of {cached.total})
                                      </Button>
                                    </Box>
                                  )}
                                </>
                              ) : null}
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Collapse>
                  </Paper>
                );
              })}
            </>
          )}
        </Box>
      </Box>

      {/* ============================================================ */}
      {/* Payload View Dialog */}
      {/* ============================================================ */}
      <Dialog
        open={payloadViewOpen}
        onClose={() => { setPayloadViewOpen(false); setPayloadViewData(null); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            boxShadow: `0 0 60px rgba(0,0,0,0.5)`,
          },
        }}
      >
        <DialogTitle sx={{
          color: colors.textPrimary,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <WebhookIcon sx={{ color: colors.info }} />
          <span>Webhook Payload</span>
          {payloadViewData && (
            <Chip
              label={payloadViewData.status?.toUpperCase()}
              size="small"
              sx={{ ml: 1, fontWeight: 700, fontSize: '0.65rem' }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {payloadViewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: colors.info }} />
            </Box>
          ) : payloadViewData ? (
            <Box>
              {/* Metadata header */}
              <Stack direction="row" spacing={3} sx={{ mb: 2, mt: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                    Store
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                    {payloadViewData.storeName || payloadViewData.externalStoreId}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                    {payloadViewData.eventType === 'item_update' ? 'Items' : 'Items / Categories'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                    {payloadViewData.eventType === 'item_update' ? payloadViewData.itemCount : `${payloadViewData.itemCount} / ${payloadViewData.categoryCount}`}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                    Size
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                    {payloadViewData.sizeKB} KB
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                    Received
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                    {payloadViewData.createdAt ? new Date(payloadViewData.createdAt).toLocaleString() : '—'}
                  </Typography>
                </Box>
                {(payloadViewData.runCount ?? 0) > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                      Runs
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                      {payloadViewData.runCount}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {payloadViewData.errorMessage && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, bgcolor: alpha(colors.error, 0.1), color: colors.error, border: `1px solid ${alpha(colors.error, 0.3)}` }}
                >
                  {payloadViewData.errorMessage}
                </Alert>
              )}

              {/* Run History + Current Result */}
              {(() => {
                const runHistory: RunHistoryEntry[] = payloadViewData.runHistory ?? [];
                const currentProgress = payloadViewData.processingProgress;
                const currentResult: ProcessingResultSummary | undefined = currentProgress?.result;
                const currentRunNumber = payloadViewData.runCount ?? 0;

                // Build the complete timeline: archived runs + current run
                // Show current run even for pre-migration payloads (run_count=0 but processing_progress exists)
                const hasCurrentRun = currentProgress && (currentProgress.completedAt || currentProgress.error || currentProgress.currentStep);
                const allRuns: Array<{
                  runNumber: number;
                  trigger: 'webhook' | 'manual';
                  startedAt: string;
                  completedAt?: string;
                  result?: ProcessingResultSummary;
                  error?: string;
                  isCurrent: boolean;
                }> = [
                  ...runHistory.map((r) => ({ ...r, isCurrent: false })),
                  ...(hasCurrentRun ? [{
                    runNumber: currentRunNumber || runHistory.length + 1,
                    trigger: ((currentRunNumber || 1) === 1 ? 'webhook' : 'manual') as 'webhook' | 'manual',
                    startedAt: currentProgress.startedAt,
                    completedAt: currentProgress.completedAt,
                    result: currentResult,
                    error: currentProgress.error,
                    isCurrent: true,
                  }] : []),
                ];

                if (allRuns.length === 0) {
                  return (
                    <Typography variant="body2" sx={{ color: colors.textMuted, py: 2 }}>
                      No processing runs yet.
                    </Typography>
                  );
                }

                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HistoryIcon sx={{ fontSize: 16 }} />
                      Run History
                    </Typography>
                    <Stack spacing={1.5}>
                      {allRuns.map((run) => (
                        <Box
                          key={run.runNumber}
                          sx={{
                            bgcolor: run.isCurrent ? alpha(colors.info, 0.06) : colors.bgDeepest,
                            border: `1px solid ${run.isCurrent ? alpha(colors.info, 0.25) : colors.border}`,
                            borderRadius: 1.5,
                            p: 1.5,
                          }}
                        >
                          {/* Run header */}
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: run.result ? 1 : 0 }}>
                            <Chip
                              icon={run.trigger === 'webhook' ? <BoltIcon sx={{ fontSize: 12 }} /> : <PlayIcon sx={{ fontSize: 12 }} />}
                              label={`Run ${run.runNumber}`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                height: 22,
                                bgcolor: run.isCurrent ? alpha(colors.info, 0.12) : alpha(colors.textMuted, 0.1),
                                color: run.isCurrent ? colors.info : colors.textSecondary,
                                '& .MuiChip-icon': { color: 'inherit' },
                              }}
                            />
                            <Chip
                              label={run.trigger === 'webhook' ? 'Automated' : 'Manual rerun'}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 20, borderColor: alpha(colors.textMuted, 0.3), color: colors.textMuted }}
                            />
                            {run.error && (
                              <Chip
                                label="FAILED"
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 20, fontWeight: 700, bgcolor: alpha(colors.error, 0.12), color: colors.error }}
                              />
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                              {new Date(run.startedAt).toLocaleString()}
                            </Typography>
                            {run.completedAt && (
                              <Chip
                                label={formatDuration(run.startedAt, run.completedAt)}
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 18, bgcolor: alpha(colors.success, 0.08), color: colors.success }}
                              />
                            )}
                          </Stack>

                          {/* Run result summary */}
                          {run.result && <ResultSummaryChips result={run.result} />}

                          {/* Error messages from processing */}
                          {run.result?.errorMessages && run.result.errorMessages.length > 0 && (
                            <Box sx={{ mt: 1, bgcolor: alpha(colors.error, 0.06), border: `1px solid ${alpha(colors.error, 0.15)}`, borderRadius: 1, p: 1 }}>
                              <Typography variant="caption" sx={{ color: colors.error, fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Errors ({run.result.errorMessages.length}):
                              </Typography>
                              {run.result.errorMessages.map((msg, i) => (
                                <Typography key={i} variant="caption" sx={{ color: colors.error, display: 'block', fontFamily: 'monospace', fontSize: '0.65rem', opacity: 0.85 }}>
                                  {msg}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          {/* Top-level run error (e.g. crash) */}
                          {run.error && (
                            <Alert severity="error" sx={{ mt: 1, py: 0, fontSize: '0.7rem', bgcolor: alpha(colors.error, 0.08), color: colors.error, border: `1px solid ${alpha(colors.error, 0.2)}` }}>
                              {run.error}
                            </Alert>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                );
              })()}

              {/* Raw payload (collapsible) */}
              <Box>
                <Button
                  size="small"
                  onClick={() => {
                    const el = document.getElementById('payload-json-viewer');
                    if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                  }}
                  sx={{ color: colors.textMuted, fontSize: '0.7rem', mb: 0.5 }}
                  startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                >
                  Toggle Raw Payload
                </Button>
                <Box
                  id="payload-json-viewer"
                  sx={{
                    display: 'none',
                    bgcolor: colors.bgDeepest,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 1,
                    p: 2,
                    maxHeight: 400,
                    overflow: 'auto',
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    fontSize: '0.75rem',
                    color: colors.textSecondary,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {JSON.stringify(payloadViewData.payload, null, 2)}
                </Box>
              </Box>
            </Box>
          ) : (
            <Alert severity="error" sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error }}>
              Failed to load payload.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button onClick={() => { setPayloadViewOpen(false); setPayloadViewData(null); }} sx={{ color: colors.textSecondary }}>
            Close
          </Button>
          {payloadViewData && (
            <>
              <Button
                onClick={() => handleRerunPayload(payloadViewData.id, payloadViewData.storeName)}
                disabled={rerunningId === payloadViewData.id}
                startIcon={rerunningId === payloadViewData.id ? <CircularProgress size={14} /> : <ReplayIcon />}
                sx={{ color: colors.warning, '&:hover': { bgcolor: alpha(colors.warning, 0.08) } }}
              >
                Rerun
              </Button>
              <Button
                onClick={() => { handleDeletePayload(payloadViewData.id); setPayloadViewOpen(false); setPayloadViewData(null); }}
                startIcon={<DeletePayloadIcon />}
                sx={{ color: colors.error, '&:hover': { bgcolor: alpha(colors.error, 0.08) } }}
              >
                Delete
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebhookPayloads;
